"use client"

import React, { useState } from "react"
import { useParams } from "react-router-dom"
import { useRestaurantAuth } from "../../contexts/RestaurantAuthContext"
import { useLanguage } from "../../contexts/LanguageContext"
import { restaurantAuthAPI } from "../../services/restaurantApiClient"
import {
  User,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Save,
  Shield,
  Clock,
  Building2,
  Globe,
  Moon,
  Sun
} from "lucide-react"
import { Box, Flex, Grid, Text, TextField, Button } from "@radix-ui/themes"
import { toast } from "react-hot-toast"
import { useTheme } from "../../contexts/ThemeContext"
import PageHeader from "../../components/ui/dashboard/PageHeader"
import DataCard from "../../components/ui/dashboard/DataCard"
import LoadingSpinner from "../../components/ui/dashboard/LoadingSpinner"

const StaffProfilePage: React.FC = () => {
  const { restaurantId } = useParams()
  const { user } = useRestaurantAuth()
  const { t, language, dir, setLanguage } = useLanguage()
  const { isDarkMode, toggleTheme } = useTheme()
  const [isEditing, setIsEditing] = useState(false)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [loading, setLoading] = useState(false)

  const [profileData, setProfileData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  })

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSaveProfile = async () => {
    setLoading(true)
    try {
      // Update profile via API if needed
      // For now, just update local state
      setIsEditing(false)
      toast.success(t('profileUpdatedSuccessfully'))
    } catch (error: any) {
      console.error("Error updating profile:", error)
      toast.error(t('failedToUpdateProfile'))
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordChange = async () => {
    if (profileData.newPassword !== profileData.confirmPassword) {
      toast.error(t('passwordsDoNotMatch'))
      return
    }

    if (profileData.newPassword.length < 6) {
      toast.error(t('passwordMinLength'))
      return
    }

    setLoading(true)
    try {
      await restaurantAuthAPI.changePassword({
        currentPassword: profileData.currentPassword,
        newPassword: profileData.newPassword
      })

      toast.success(t('passwordUpdatedSuccessfully'))
      setProfileData(prev => ({
        ...prev,
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      }))
    } catch (error: any) {
      console.error("Error changing password:", error)
      const errorMessage = error?.response?.data?.message || error?.message || t('failedToUpdatePassword')
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const getRoleDisplayName = (role: string) => {
    const roleMap: Record<string, string> = {
      manager: t("manager"),
      cashier: t("cashier"),
      waiter: t("waiter"),
      kitchen_staff: t("kitchenStaff"),
      admin: t("administrator"),
    }
    return roleMap[role] || role
  }

  return (
    <div className="space-y-6" dir={dir}>
      <PageHeader
        title={t("myProfile")}
        subtitle={t("manageYourPersonalInformation")}
      />

      {/* Owner Information */}
      <DataCard
        title={
          <div className={`flex items-center ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <User className={`w-5 h-5 text-blue-600 ${language === 'ar' ? 'ml-2' : 'mr-2'}`} />
            <span>{t("personalInformation")}</span>
          </div>
        }
      >
        <Grid columns={{ initial: "1", md: "2" }} gap="4">
          <Box>
            <Text as="label" size="2" weight="medium" mb="2">
              {t("fullName")}
            </Text>
            {isEditing ? (
              <TextField.Root
                name="name"
                value={profileData.name}
                onChange={handleInputChange}
                placeholder={t("enterYourName")}
                size="3"
              />
            ) : (
              <Text size="3">{profileData.name || t("notSet")}</Text>
            )}
          </Box>

          <Box>
            <Text as="label" size="2" weight="medium" mb="2">
              {t("emailAddress")}
            </Text>
            {isEditing ? (
              <TextField.Root
                name="email"
                type="email"
                value={profileData.email}
                onChange={handleInputChange}
                placeholder={t("enterYourEmail")}
                size="3"
              >
                <TextField.Slot side={language === 'ar' ? 'right' : 'left'}>
                  <Mail size={18} />
                </TextField.Slot>
              </TextField.Root>
            ) : (
              <Text size="3">{profileData.email || t("notSet")}</Text>
            )}
          </Box>

          <Box>
            <Text as="label" size="2" weight="medium" mb="2">
              {t("phoneNumber")}
            </Text>
            {isEditing ? (
              <TextField.Root
                name="phone"
                value={profileData.phone}
                onChange={handleInputChange}
                placeholder={t("enterYourPhone")}
                size="3"
              />
            ) : (
              <Text size="3">{profileData.phone || t("notSet")}</Text>
            )}
          </Box>

          <Box>
            <Text as="label" size="2" weight="medium" mb="2">
              {t("role")}
            </Text>
            <Text size="3">{getRoleDisplayName(user?.role || "")}</Text>
          </Box>
        </Grid>

        <Flex gap="3" mt="4" style={{ flexDirection: language === 'ar' ? 'row-reverse' : 'row' }}>
          {isEditing ? (
            <>
              <Button
                variant="outline"
                size="3"
                onClick={() => setIsEditing(false)}
              >
                {t("cancel")}
              </Button>
              <Button
                variant="solid"
                size="3"
                onClick={handleSaveProfile}
                disabled={loading}
              >
                {loading ? <LoadingSpinner size="1" /> : t("saveChanges")}
              </Button>
            </>
          ) : (
            <Button
              variant="solid"
              size="3"
              onClick={() => setIsEditing(true)}
            >
              {t("editProfile")}
            </Button>
          )}
        </Flex>
      </DataCard>

      {/* Security Settings */}
      <DataCard
        title={
          <div className={`flex items-center ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <Shield className={`w-5 h-5 text-green-600 ${language === 'ar' ? 'ml-2' : 'mr-2'}`} />
            <span>{t("securitySettings")}</span>
          </div>
        }
      >
        <Grid columns={{ initial: "1", md: "2" }} gap="4">
          <Box>
            <Text as="label" size="2" weight="medium" mb="2">
              {t("currentPassword")}
            </Text>
            <TextField.Root
              name="currentPassword"
              type={showCurrentPassword ? "text" : "password"}
              value={profileData.currentPassword}
              onChange={handleInputChange}
              placeholder={t("enterCurrentPassword")}
              size="3"
            >
              <TextField.Slot side={language === 'ar' ? 'right' : 'left'}>
                <Lock size={18} />
              </TextField.Slot>
              <TextField.Slot side={language === 'ar' ? 'left' : 'right'}>
                <Button
                  type="button"
                  variant="ghost"
                  size="1"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </Button>
              </TextField.Slot>
            </TextField.Root>
          </Box>

          <Box>
            <Text as="label" size="2" weight="medium" mb="2">
              {t("newPassword")}
            </Text>
            <TextField.Root
              name="newPassword"
              type={showNewPassword ? "text" : "password"}
              value={profileData.newPassword}
              onChange={handleInputChange}
              placeholder={t("enterNewPassword")}
              size="3"
            >
              <TextField.Slot side={language === 'ar' ? 'right' : 'left'}>
                <Lock size={18} />
              </TextField.Slot>
              <TextField.Slot side={language === 'ar' ? 'left' : 'right'}>
                <Button
                  type="button"
                  variant="ghost"
                  size="1"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </Button>
              </TextField.Slot>
            </TextField.Root>
          </Box>

          <Box>
            <Text as="label" size="2" weight="medium" mb="2">
              {t("confirmPassword")}
            </Text>
            <TextField.Root
              name="confirmPassword"
              type={showNewPassword ? "text" : "password"}
              value={profileData.confirmPassword}
              onChange={handleInputChange}
              placeholder={t("confirmNewPassword")}
              size="3"
            >
              <TextField.Slot side={language === 'ar' ? 'right' : 'left'}>
                <Lock size={18} />
              </TextField.Slot>
            </TextField.Root>
          </Box>
        </Grid>

        <Flex gap="3" mt="4" style={{ flexDirection: language === 'ar' ? 'row-reverse' : 'row' }}>
          <Button
            variant="solid"
            size="3"
            onClick={handlePasswordChange}
            disabled={loading || !profileData.currentPassword || !profileData.newPassword}
          >
            {loading ? <LoadingSpinner size="1" /> : t("updatePassword")}
          </Button>
        </Flex>
      </DataCard>

      {/* Account Information */}
      <DataCard
        title={
          <div className={`flex items-center ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <Building2 className={`w-5 h-5 text-purple-600 ${language === 'ar' ? 'ml-2' : 'mr-2'}`} />
            <span>{t("accountInformation")}</span>
          </div>
        }
      >
        <Grid columns={{ initial: "1", md: "2" }} gap="4">
          <Box>
            <Flex align="center" gap="3" style={{ flexDirection: language === 'ar' ? 'row-reverse' : 'row' }}>
              <Clock className="w-5 h-5 text-gray-400" />
              <Box>
                <Text size="2" weight="medium">{t("memberSince")}</Text>
                <Text size="2" color="gray">{t("notAvailable")}</Text>
              </Box>
            </Flex>
          </Box>
          <Box>
            <Flex align="center" gap="3" style={{ flexDirection: language === 'ar' ? 'row-reverse' : 'row' }}>
              <Building2 className="w-5 h-5 text-gray-400" />
              <Box>
                <Text size="2" weight="medium">{t("accountType")}</Text>
                <Text size="2" color="gray">{getRoleDisplayName(user?.role || "")}</Text>
              </Box>
            </Flex>
          </Box>
        </Grid>
      </DataCard>

      {/* Appearance & Preferences Section */}
      <DataCard
        title={t("appearancePreferences")}
        subtitle={t("customizeYourInterfacePreferences")}
      >
        <Text size="2" color="gray" mb="4">
          {t("appearancePreferencesDescription")}
        </Text>

        <Grid columns={{ initial: "1", md: "2" }} gap="4">
          {/* Language Preference */}
          <Box style={{ backgroundColor: 'var(--gray-3)', padding: '16px', borderRadius: 'var(--radius-3)' }}>
            <Flex direction="column" gap="3">
              <Flex align="center" gap="3" mb="2" style={{ flexDirection: language === 'ar' ? 'row-reverse' : 'row' }}>
                <Globe className="h-5 w-5" style={{ color: 'var(--gray-11)' }} />
                <Box>
                  <Text size="2" weight="medium" mb="1">
                    {t("language")}
                  </Text>
                  <Text size="1" color="gray">
                    {t("selectYourPreferredLanguage")}
                  </Text>
                </Box>
              </Flex>
              <Flex gap="2" style={{ flexDirection: language === 'ar' ? 'row-reverse' : 'row' }}>
                <Button
                  variant={language === 'en' ? 'solid' : 'outline'}
                  color={language === 'en' ? 'red' : 'gray'}
                  size="2"
                  onClick={() => setLanguage('en')}
                >
                  {t("english")}
                </Button>
                <Button
                  variant={language === 'ar' ? 'solid' : 'outline'}
                  color={language === 'ar' ? 'red' : 'gray'}
                  size="2"
                  onClick={() => setLanguage('ar')}
                >
                  {t("arabic")}
                </Button>
              </Flex>
            </Flex>
          </Box>

          {/* Theme Preference */}
          <Box style={{ backgroundColor: 'var(--gray-3)', padding: '16px', borderRadius: 'var(--radius-3)' }}>
            <Flex direction="column" gap="3">
              <Flex align="center" gap="3" mb="2" style={{ flexDirection: language === 'ar' ? 'row-reverse' : 'row' }}>
                {isDarkMode ? (
                  <Sun className="h-5 w-5" style={{ color: 'var(--gray-11)' }} />
                ) : (
                  <Moon className="h-5 w-5" style={{ color: 'var(--gray-11)' }} />
                )}
                <Box>
                  <Text size="2" weight="medium" mb="1">
                    {t("theme")}
                  </Text>
                  <Text size="1" color="gray">
                    {t("switchBetweenLightAndDarkMode")}
                  </Text>
                </Box>
              </Flex>
              <Flex gap="2" style={{ flexDirection: language === 'ar' ? 'row-reverse' : 'row' }}>
                <Button
                  variant={!isDarkMode ? 'solid' : 'outline'}
                  color={!isDarkMode ? 'red' : 'gray'}
                  size="2"
                  onClick={() => !isDarkMode || toggleTheme()}
                >
                  <Sun className="h-4 w-4" style={{ marginRight: language === 'ar' ? '0' : '8px', marginLeft: language === 'ar' ? '8px' : '0' }} />
                  {t("light")}
                </Button>
                <Button
                  variant={isDarkMode ? 'solid' : 'outline'}
                  color={isDarkMode ? 'red' : 'gray'}
                  size="2"
                  onClick={() => isDarkMode || toggleTheme()}
                >
                  <Moon className="h-4 w-4" style={{ marginRight: language === 'ar' ? '0' : '8px', marginLeft: language === 'ar' ? '8px' : '0' }} />
                  {t("dark")}
                </Button>
              </Flex>
            </Flex>
          </Box>
        </Grid>
      </DataCard>
    </div>
  )
}

export default StaffProfilePage


